import { ElementRef, Injectable } from '@angular/core';
import { documentToHtmlString } from '@contentful/rich-text-html-renderer';
import { documentToPlainTextString } from '@contentful/rich-text-plain-text-renderer';


@Injectable()
export class LcpHelper {

  static defaultListPlaceholderHTML: string = "<p><em>no results</em></p>"

  stopAllTyping: boolean
  isTypingElements: Element[] = []

  htmlFromRichText(richText: any) {
    if (richText === undefined || richText === null || richText.nodeType !== 'document') {
      return '<p>Error</p>';
    }
    return documentToHtmlString(richText);
  }

  backgroundImageFromMedia(media: any) {
    let url = this.urlFromMedia(media)
    if (!url) return
    return "url(" + url + ")"
  }

  srcFromMedia(media: any) {
    return this.urlFromMedia(media)
  }

  // raster images are served through the contentful images api, resized and re-encoded as webp
  // (originals are up to 2880px / 4MB); svg, gif, video and other files are returned unchanged
  // https://www.contentful.com/developers/docs/references/images-api/
  static IMAGE_MAX_WIDTH = 2000
  static IMAGE_SRCSET_WIDTHS = [480, 800, 1200, 1600, 2000]

  urlFromMedia(media: any, width = LcpHelper.IMAGE_MAX_WIDTH) {
    let file = media?.fields?.file
    if (!file?.url) return
    let url = 'https:' + file.url
    if (!LcpHelper.isResizableImage(file)) return url
    let originalWidth = file.details?.image?.width
    return LcpHelper.resizedImageUrl(url, originalWidth ? Math.min(width, originalWidth) : width)
  }

  // for <img [attr.srcset]>, paired with a `sizes` attribute; null when the file can't be resized
  srcsetFromMedia(media: any) {
    let file = media?.fields?.file
    if (!file?.url || !LcpHelper.isResizableImage(file)) return null
    let url = 'https:' + file.url
    let originalWidth = file.details?.image?.width || LcpHelper.IMAGE_MAX_WIDTH
    let widths = LcpHelper.IMAGE_SRCSET_WIDTHS.filter(w => w < originalWidth)
    widths.push(Math.min(originalWidth, LcpHelper.IMAGE_MAX_WIDTH))
    return widths.map(w => `${LcpHelper.resizedImageUrl(url, w)} ${w}w`).join(', ')
  }

  static isResizableImage(file: any) {
    return /^image\/(jpeg|png|webp)$/.test(file.contentType) && /\/\/images\.ctfassets\.net\//.test(file.url)
  }

  static resizedImageUrl(url: string, width: number) {
    return `${url}?w=${width}&fm=webp&q=80`
  }

  // https://stackoverflow.com/questions/1960473/get-all-unique-values-in-a-javascript-array-remove-duplicates
  // usage example:
  // var a = ['a', 1, 'a', 2, '1'];
  // var unique = a.filter(onlyUnique);
  onlyUnique(value: any, index: any, array: any) {
    return array.indexOf(value) === index;
  }

  getRandomInt(max: number) {
    return Math.floor(Math.random() * max);
  }

  removeFromGroup(item: any, group: any) {
    const itemIndex = group.indexOf(item);
    console.log('ind', itemIndex, group, item)
    if (itemIndex > -1) { // only splice array when item is found
      group.splice(itemIndex, 1); // 2nd parameter means remove one item only
    }
    return group
  }

  // USAGE:
  // obj = { barney: { age: 36, active: true },
  //     fred: { age: 40, active: false },
  //     pebbles: { age: 1, active: true } }
  // findKey( obj,  x => x['active']); // returns 'barney'
  findKey(obj: any, fn: any) {
    if (!obj) return
    Object.keys(obj).find(key => fn(obj[key], key, obj));
  }

  percentageSequence(keys = 100) {
    let scale = keys/100
    let sequence = Array.from(Array(keys).keys()).map(i => parseFloat(((i+1) * (0.01 / scale)).toFixed(3)))
    // console.log('percentageSequence', sequence)
    return sequence
  }

  delay(ms: number) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  blockClass(block: any): any {
    try {
      return block.fields['slug'] || ''
    } catch { 
      return ''
    }
  }

  // stopTypingElement(el: Element) {
  //   this.isTypingElements = this.isTypingElements.filter(i => i !== el)
  // }

  async typeSentence(sentence: any, el: Element, params={}) {
    // only type once; do not remove (8/29/25)
    // if (this.isTypingElements.includes(el)) return
    // this.isTypingElements.push(el)

    // @ts-ignore
    let delay = params['delay'] || 100
    let minDelay = params['minDelay'] || 10
    const letters = sentence.split("");
    let i = 0;
    // this.isTypingElements.push(el)
    // while(i < letters.length && this.isTypingElements.includes(el)) {
    // while(i < letters.length && this.isTypingElements.includes(el)) {
    while(i < letters.length) {

      // if (!this.isTypingElements.includes(el)) return
      let randomizedDelay = Math.max(Math.random() * delay, minDelay);
      // console.log('randy', randomizedDelay)
      await this.waitForMs(randomizedDelay);
      this.typeLetter(el, letters[i], params)
      // $(el).append(letters[i]);
      i++
    }
    // this.stopTypingElement(el)
    // console.log('isTypingElements', this.isTypingElements)
    return
  }

  typeLetter(el: Element, letter: string, params={}) {
    // let el = this.createElement('span', letter)
    const shouldAnimate = params['shouldType']
    if (!shouldAnimate) return
    // let fadeIn = params['fade-in']
    // let container = el.nativeElement
    el.insertAdjacentHTML('beforeend', '<span>' + letter + '</span>')
    // if (params['fade-in'])
  }

  waitForMs(ms: number) {
    return new Promise(resolve => setTimeout(resolve, ms))
  }

  richTextToString(document) {
    return documentToPlainTextString(document)
  }

  linkFromBlock(block: any) {
    // console.log('TODO: linkFromBlock', block)
    const text = block.fields['linkText']
    const address = block.fields['linkAddress']
    if (!text || !address) return
    return {
      text: text,
      address: address
    }
  }

  primaryColorHex(hexText) {
    if (!hexText) return hexText
    if (hexText[0] == '#') return hexText
    return `#${hexText}`
  }

  // from https://stackoverflow.com/questions/5623838/rgb-to-hex-and-hex-to-rgb
  colorIsDarkAdvanced(bgColor) {
    let L = this.colorLuminosity(bgColor);
    // console.log('CHECK c', c, uicolors, L)
    // return L <= 0.179
    return L <= 0.35
  }

  colorLuminosity(bgColor): number {
    let color = (bgColor.charAt(0) === '#') ? bgColor.substring(1, 7) : bgColor
    let r = parseInt(color.substring(0, 2), 16) // hexToR
    let g = parseInt(color.substring(2, 4), 16) // hexToG
    let b = parseInt(color.substring(4, 6), 16) // hexToB
    let uicolors = [r / 255, g / 255, b / 255]
    let c = uicolors.map((col) => {
      if (col <= 0.03928) {
        return col / 12.92
      }
      return Math.pow((col + 0.055) / 1.055, 2.4)
    })
    return (0.2126 * c[0]) + (0.7152 * c[1]) + (0.0722 * c[2])
  }

  normalizedHex(val: string) {
    if (!val) return val
    if (val[0] == '#') return val
    return `#${val}`
  }
    
  // filterDisplay(filter: any) {
  //   filter.fields['name']
  // }
}